import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, downloadUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { FileText, Download, Trash2, Plus, Upload, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useDoubleConfirm, useConfirm } from "@/components/ConfirmProvider";
import { MODULI } from "@/components/StampaMandatoDialog";

const MODULO_LABELS = Object.fromEntries(Object.entries(MODULI).map(([k, v]) => [k, v.label]));

/**
 * Sprint 2B-Anagrafiche: componente Documenti riutilizzabile.
 * Props:
 *  - ownerType: "vehicle" | "supplier" | "customer" | "pratica"
 *  - ownerId: string (UUID)
 *  - companyId: string | null (necessario per vehicle/pratica; null per supplier/customer)
 *  - canUpload: bool
 *  - canDelete: bool
 */
export default function DocumentsTab({ ownerType, ownerId, companyId, canUpload, canDelete }) {
  const confirm = useDoubleConfirm();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openUpload, setOpenUpload] = useState(false);
  const [collapsed, setCollapsed] = useState({}); // { categoryName: bool }

  const load = async () => {
    if (!ownerId) return;
    setLoading(true);
    try {
      // Use polymorphic list: GET /api/{owner_type_plural}/{id}/documents
      const plural = ownerType === "vehicle" ? "vehicles" : `${ownerType}s`;
      const r = await api.get(`/${plural}/${ownerId}/documents`);
      const data = r.data;
      const list = Array.isArray(data) ? data : (data?.items || []);
      setDocs(list);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento documenti");
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [ownerType, ownerId]);

  const grouped = useMemo(() => {
    const m = {};
    for (const d of docs) {
      const key = d.category_name || "—";
      (m[key] = m[key] || []).push(d);
    }
    return m;
  }, [docs]);

  const toggle = (cat) => setCollapsed((c) => ({ ...c, [cat]: !c[cat] }));

  const onDelete = async (d) => {
    const ok = await confirm({
      variant: "delete", title: "Elimina documento",
      message: `Stai per eliminare "${d.display_name || d.original_filename}".`,
      confirmLabel: "SI, elimina",
    });
    if (!ok) return;
    const motivo = window.prompt("Motivo dell'eliminazione (obbligatorio):") || "";
    if (motivo.trim().length < 5) { toast.error("Motivo obbligatorio (min 5 caratteri)"); return; }
    try {
      await api.delete(`/documents/${d.id}`, { data: { motivo } });
      toast.success("Documento eliminato");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <Card className="p-4 border border-slate-200 shadow-sm bg-white" data-testid="documents-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="font-heading text-base font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" /> Documenti
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">{docs.length} documento/i</p>
        </div>
        {canUpload && (
          <Button onClick={() => setOpenUpload(true)} className="bg-blue-600 hover:bg-blue-700"
                  data-testid="docs-upload-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Carica documento
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-400">Caricamento...</div>
      ) : docs.length === 0 ? (
        <div className="text-center py-8 text-slate-400 italic" data-testid="docs-empty">
          Nessun documento caricato
        </div>
      ) : (
        <div className="space-y-2">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggle(cat)}
                className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 text-left text-sm font-medium"
                data-testid={`docs-cat-toggle-${cat}`}
              >
                <span className="flex items-center gap-2">
                  {collapsed[cat] ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  {cat}
                </span>
                <span className="text-xs text-slate-500">{items.length}</span>
              </button>
              {!collapsed[cat] && (
                <div className="divide-y divide-slate-100">
                  {items.map((d) => (
                    <div key={d.id} className="px-3 py-2 flex items-center gap-2 text-sm hover:bg-slate-50/50"
                         data-testid={`docs-row-${d.id}`}>
                      <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                      <a
                        href={downloadUrl(d.id)}
                        target="_blank" rel="noreferrer"
                        className="flex-1 truncate text-slate-700 hover:text-blue-600 hover:underline"
                        title={d.original_filename}
                      >
                        {d.display_name || d.original_filename}
                      </a>
                      <span className="text-[11px] text-slate-400 hidden sm:inline">
                        {d.size ? `${(d.size / 1024).toFixed(1)} KB` : "—"}
                      </span>
                      <span className="text-[11px] text-slate-400 hidden md:inline">
                        {(d.created_at || "").slice(0, 10)}
                      </span>
                      <a href={downloadUrl(d.id)} className="text-slate-500 hover:text-blue-600"
                         title="Scarica" data-testid={`docs-download-${d.id}`}>
                        <Download className="h-4 w-4" />
                      </a>
                      {canDelete && (
                        <button onClick={() => onDelete(d)} className="text-rose-500 hover:text-rose-700"
                                title="Elimina" data-testid={`docs-delete-${d.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <UploadDocModal
        open={openUpload}
        onOpenChange={setOpenUpload}
        ownerType={ownerType}
        ownerId={ownerId}
        companyId={companyId}
        onUploaded={load}
      />

      {["vehicle", "pratica"].includes(ownerType) && (
        <GeneratedDocsSection ownerType={ownerType} ownerId={ownerId} canDelete={canDelete} />
      )}
    </Card>
  );
}

function GeneratedDocsSection({ ownerType, ownerId, canDelete }) {
  const confirm = useConfirm();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!ownerId) return;
    setLoading(true);
    const out = [];
    try {
      const r = await api.get(`/stampe/documenti/${ownerType}/${ownerId}`);
      const items = r.data?.items || [];
      if (items.length) out.push({ key: "own", title: null, items });
    } catch { /* nessun documento generato o endpoint non applicabile */ }
    if (ownerType === "vehicle") {
      try {
        const pr = await api.get(`/vendite?vehicle_id=${ownerId}&page_size=50`);
        for (const p of (pr.data?.items || [])) {
          try {
            const r = await api.get(`/stampe/documenti/vendita/${p.id}`);
            const items = r.data?.items || [];
            if (items.length) out.push({ key: p.id, title: p.codice || p.id, tipo: p.tipo, items });
          } catch { /* pratica non visibile */ }
        }
      } catch { /* nessuna pratica */ }
    }
    setGroups(out);
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [ownerType, ownerId]);

  const onApri = async (docId) => {
    try {
      const r = await api.get(`/stampe/documenti/${docId}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Impossibile aprire il documento archiviato"); }
  };

  const onElimina = async (d) => {
    const ok = await confirm({
      variant: "delete",
      title: "Elimina documento generato",
      message: `Eliminare "${MODULO_LABELS[d.modulo] || d.modulo}" versione ${d.versione}? L'operazione è definitiva: il documento non sarà più apribile. La numerazione delle versioni resta progressiva.`,
      confirmLabel: "SI, elimina",
    });
    if (!ok) return;
    try {
      await api.delete(`/stampe/documenti/${d.id}`);
      toast.success("Documento eliminato");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore eliminazione"); }
  };

  if (loading || groups.length === 0) return null;

  return (
    <div className="mt-6 border-t pt-4" data-testid="generated-docs-section">
      <h3 className="font-heading text-sm font-semibold flex items-center gap-2 mb-2">
        <FileText className="h-4 w-4 text-blue-600" /> Documenti generati (stampe)
      </h3>
      {groups.map((g) => (
        <div key={g.key} className="mb-3" data-testid={`gendoc-group-${g.key}`}>
          {g.title && (
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1"
                 data-testid={`gendoc-pratica-${g.key}`}>
              Documenti {g.tipo === "preventivo" ? "del preventivo" : "della pratica"} {g.title}
            </div>
          )}
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
            {g.items.map((d) => (
              <div key={d.id} className="px-3 py-2 flex items-center gap-2 text-sm hover:bg-slate-50/50"
                   data-testid={`gendoc-row-${d.id}`}>
                <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                <button onClick={() => onApri(d.id)}
                        className="flex-1 truncate text-left text-slate-700 hover:text-blue-600 hover:underline"
                        data-testid={`gendoc-open-${d.id}`}>
                  {MODULO_LABELS[d.modulo] || d.modulo} — v{d.versione}{g.title ? ` · ${g.tipo === "preventivo" ? "preventivo" : "pratica"} ${g.title}` : ""}
                </button>
                <span className="text-[11px] text-slate-400 hidden md:inline">{d.generato_da_email || "—"}</span>
                <span className="text-[11px] text-slate-400 hidden sm:inline">{(d.created_at || "").slice(0, 10)}</span>
                <button onClick={() => onApri(d.id)} className="text-slate-500 hover:text-blue-600"
                        title="Apri" data-testid={`gendoc-download-${d.id}`}>
                  <Download className="h-4 w-4" />
                </button>
                {canDelete && (
                  <button onClick={() => onElimina(d)} className="text-rose-500 hover:text-rose-700"
                          title="Elimina" data-testid={`gendoc-delete-${d.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function UploadDocModal({ open, onOpenChange, ownerType, ownerId, companyId, onUploaded }) {
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  // Sprint 2D-PART-B fix: subcategorie disponibili sempre, ma OBBLIGATORIE solo per pratica.
  const requiresSubcategory = ownerType === "pratica";

  // Subcategories from selected category (nested response from /api/categories)
  const subcategories = categoryId
    ? (categories.find((c) => c.id === categoryId)?.subcategories || [])
    : [];

  useEffect(() => {
    if (!open) return;
    api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
    setCategoryId(""); setSubcategoryId(""); setFile(null);
  }, [open]);

  // reset subcategory when category changes
  useEffect(() => { setSubcategoryId(""); }, [categoryId]);

  const onSubmit = async () => {
    if (!file) { toast.error("Seleziona un file"); return; }
    if (!categoryId) { toast.error("Seleziona una categoria"); return; }
    if (requiresSubcategory && !subcategoryId) { toast.error("Seleziona una sottocategoria"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("owner_type", ownerType);
      fd.append("owner_id", ownerId);
      fd.append("category_id", categoryId);
      if (subcategoryId) fd.append("subcategory_id", subcategoryId);
      if (companyId) fd.append("company_id", companyId);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato");
      onUploaded?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore upload");
    } finally { setSaving(false); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Carica documento</DialogTitle>
          <DialogDescription>
            Allegato a {ownerType === "vehicle" ? "veicolo" : ownerType === "supplier" ? "fornitore" : ownerType === "customer" ? "cliente" : ownerType === "anagrafica" ? "anagrafica" : "pratica"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Categoria *</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger data-testid="docs-upload-category"><SelectValue placeholder="Seleziona categoria" /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {categoryId && subcategories.length > 0 && (
            <div>
              <Label className="text-xs">Sottocategoria{requiresSubcategory ? " *" : " (opzionale)"}</Label>
              <Select value={subcategoryId} onValueChange={setSubcategoryId}>
                <SelectTrigger data-testid="docs-upload-subcategory"><SelectValue placeholder="Seleziona sottocategoria" /></SelectTrigger>
                <SelectContent>
                  {subcategories.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">File *</Label>
            <div
              onDrop={onDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => inputRef.current?.click()}
              className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30"
              data-testid="docs-upload-drop"
            >
              <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
              {file ? (
                <div className="text-sm text-slate-700 font-medium truncate">{file.name}</div>
              ) : (
                <>
                  <div className="text-sm text-slate-600">Trascina qui o clicca per selezionare</div>
                  <div className="text-xs text-slate-400 mt-1">PDF, immagini, Word, Excel...</div>
                </>
              )}
              <Input ref={inputRef} type="file" className="hidden"
                     onChange={(e) => setFile(e.target.files?.[0] || null)}
                     data-testid="docs-upload-file" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annulla</Button>
          <Button onClick={onSubmit} disabled={saving} className="bg-blue-600 hover:bg-blue-700"
                  data-testid="docs-upload-submit">
            {saving ? "Caricamento..." : "Carica"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
